/**
 * Home time and this phone's time. Dates are calendar days everywhere, and timed tasks are home
 * time (the household's zone): this phone shows a home time as the same moment on its own clock,
 * and a time typed here is saved as home time. "Today" is where the phone is. At home the two
 * zones are the same and nothing changes.
 */
import { useEffect, useState } from 'react'
import { convertWallTime, isValidTimeZone } from '@terrazzo/core'
import { store } from '../store/store'

// This phone's zone, read once and again whenever the app comes back (or each minute): every row
// asks for it, and reading it from the browser is slow.
const readZone = () => {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
  return tz && isValidTimeZone(tz) ? tz : ''
}
let zone = readZone()
if (typeof window !== 'undefined') {
  const refresh = () => (zone = readZone())
  document.addEventListener('visibilitychange', refresh)
  window.addEventListener('focus', refresh)
  setInterval(refresh, 60_000)
}

/** This phone's time zone (it changes when the phone travels). */
export function deviceZone(): string {
  return zone || store.household().timezone
}

/** The household's (home) time zone: timed tasks and shared things. */
export function homeZone(): string {
  return store.household().timezone
}

/** A home date & time as this phone's clock shows it (the day can differ near midnight). */
export function toLocal(date: string, time: string): { date: string; time: string } {
  return convertWallTime(date, time, homeZone(), deviceZone())
}

/** A date & time typed on this phone, as home time (what's saved). */
export function toHome(date: string, time: string): { date: string; time: string } {
  return convertWallTime(date, time, deviceZone(), homeZone())
}

/** Just the time, as this phone shows a home time on that date. */
export function localTime(date: string | undefined, time: string): string {
  return date ? toLocal(date, time).time : time
}

/** Re-renders on each new minute (and when the app comes back), so "now" on a chart moves by itself. */
export function useMinuteTick(): number {
  const [n, setN] = useState(0)
  useEffect(() => {
    let t: ReturnType<typeof setTimeout>
    const next = () => {
      t = setTimeout(() => (setN((x) => x + 1), next()), 60_000 - (Date.now() % 60_000) + 50)
    }
    next()
    const back = () => document.visibilityState === 'visible' && setN((x) => x + 1)
    document.addEventListener('visibilitychange', back)
    return () => (clearTimeout(t), document.removeEventListener('visibilitychange', back))
  }, [])
  return n
}
